import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Clock, Video, MapPin, Users, CheckCircle2 } from 'lucide-react';
import { config } from '../../config';

const CalendarView = ({ userMetadata }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);

    const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    useEffect(() => {
        fetchAppointments();
    }, [currentDate]);

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            // In a real app, this would fetch from Hub/Dexie
            // Mocking for now to show UI capability
            const mock = [
                { id: 1, title: 'Strategic Review', time: '10:00 AM', attendee: 'John Doe', type: 'Zoom' },
                { id: 2, title: 'Growth Alignment', time: '02:30 PM', attendee: 'Sarah Jenkins', type: 'In-Person' }
            ];
            setAppointments(mock);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const renderHeader = () => {
        const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        return (
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <div className="bg-blue-600 p-3 rounded-2xl text-white shadow-xl shadow-blue-100">
                        <CalendarIcon size={24} />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 tracking-tight leading-none uppercase">
                            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                        </h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-1">High-Performance Scheduling</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))} className="p-2 hover:bg-slate-100 rounded-xl transition-all border border-slate-100">
                        <ChevronLeft size={20} className="text-slate-600" />
                    </button>
                    <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 hover:bg-slate-100 rounded-xl transition-all border border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Today
                    </button>
                    <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))} className="p-2 hover:bg-slate-100 rounded-xl transition-all border border-slate-100">
                        <ChevronRight size={20} className="text-slate-600" />
                    </button>
                </div>
            </div>
        );
    };

    const renderGrid = () => {
        const days = daysInMonth(currentDate.getFullYear(), currentDate.getMonth());
        const start = firstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
        const grid = [];

        // Adornment: Weekday labels
        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-[2.5rem] overflow-hidden border border-slate-100 shadow-xl shadow-slate-100/50">
                {weekDays.map(d => (
                    <div key={d} className="bg-slate-50 p-4 text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 text-center border-b border-slate-100">
                        {d}
                    </div>
                ))}
                {Array(start).fill(null).map((_, i) => (
                    <div key={`empty-${i}`} className="bg-slate-50/50 h-32" />
                ))}
                {Array(days).fill(null).map((_, i) => {
                    const day = i + 1;
                    const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();
                    return (
                        <div key={day} className={`bg-white h-32 p-4 relative group cursor-pointer hover:bg-blue-50/30 transition-all ${isToday ? 'bg-blue-50/20' : ''}`}>
                            <span className={`text-xs font-black tabular-nums transition-colors ${isToday ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-600'}`}>
                                {day < 10 ? `0${day}` : day}
                            </span>
                            {isToday && <div className="absolute top-4 right-4 w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse shadow-lg shadow-blue-500/50" />}

                            {/* Marker for events (simplified dots for now) */}
                            <div className="mt-2 space-y-1">
                                {appointments.length > 0 && day % 3 === 0 && (
                                    <div className="bg-blue-100/50 text-blue-600 px-2 py-1 rounded-lg text-[8px] font-black uppercase tracking-tighter truncate border border-blue-100">
                                        {appointments[0].title}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="p-8 pb-24 max-w-7xl mx-auto space-y-8">
            {renderHeader()}

            <div className="flex flex-col lg:flex-row gap-8">
                {/* Main Calendar Grid */}
                <div className="flex-1">
                    {renderGrid()}
                </div>

                {/* Side Panel: Today's Agenda */}
                <div className="lg:w-80 space-y-6">
                    <div className="bg-white rounded-[2rem] p-6 border border-slate-100 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest">Today's Agenda</h3>
                            <button className="bg-slate-900 text-white p-2 rounded-xl hover:bg-slate-800 transition-all shadow-lg active:scale-95">
                                <Plus size={16} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {appointments.map(app => (
                                <div key={app.id} className="group p-4 bg-slate-50 border border-transparent hover:border-blue-100 hover:bg-white rounded-[1.5rem] transition-all cursor-pointer">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="bg-blue-100 p-2 rounded-lg text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                                            <Clock size={14} />
                                        </div>
                                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-tighter">{app.time}</span>
                                    </div>
                                    <h4 className="text-xs font-bold text-slate-800 mb-1">{app.title}</h4>
                                    <div className="flex items-center gap-2">
                                        <span className="text-[9px] font-bold text-slate-400 capitalize">{app.type}</span>
                                        <div className="w-1 h-1 bg-slate-200 rounded-full" />
                                        <span className="text-[9px] font-bold text-slate-400">{app.attendee}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-[2rem] p-6 text-white shadow-xl shadow-slate-200 overflow-hidden relative group">
                        <div className="absolute -right-4 -top-4 opacity-10 group-hover:scale-110 transition-transform">
                            <CheckCircle2 size={120} />
                        </div>
                        <div className="relative z-10">
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Availability</h3>
                            <p className="text-sm font-bold opacity-80 leading-relaxed">
                                You are <span className="text-blue-400">75%</span> booked for this week. Great momentum!
                            </p>
                            <button className="w-full mt-6 bg-white/10 hover:bg-white/20 border border-white/10 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">
                                Sync Google Calendar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarView;
